<script lang="ts">
// this must be exported here for the keep alive on reroute
export default {
  name: "RegionDetail",
};
</script>
<script setup lang="ts">
import DealerService, { type TDealer } from "@/api/dealer";
import RegionService, { DefaultRegion, type TRegion } from "@/api/region";
import ServiceService, { type TService } from "@/api/service";
import DealersTable from "@/components/models/dealer/DealersTable.vue";
import AddEditRegionForm from "@/components/models/region/AddEditRegionForm.vue";
import ServicesTable from "@/components/models/service/ServicesTable.vue";
import { i18n } from "@/locales";
import { ActionsEnum, SubjectsEnum, type AppAbility } from "@/services/ability";
import { useSnackStore } from "@/stores/snack";
import { generateFormOptions, type PaginatedType, type VFormFieldChoice, type VFormOptions } from "@/utils/DjangoTypes";
import { DefaultServerTableParams, type ServerTableParamsType } from "@/utils/TableUtils";
import { useAbility } from "@casl/vue";
import { useQuery } from "@tanstack/vue-query";
import { definePage } from "unplugin-vue-router/runtime";
import { onMounted, provide, ref } from "vue";
import { useRoute } from "vue-router";

const route = useRoute();

const currentRegionId = Number(route.params.id);

definePage({
  meta: {
    title: "Detail of region",
    detailOf: "region",
    checkPrivileges: () => {
      const { can } = useAbility<AppAbility>();
      return can(ActionsEnum.USE, SubjectsEnum.REGION);
    },
  },
});

const { t } = i18n.global;

const snackStore = useSnackStore();
const fetching = ref(true);

const entity = ref<TRegion>(DefaultRegion);
const options = ref<VFormOptions>({});

const serviceTypeChoices = ref<VFormFieldChoice<any>[]>([]);

const queriesTableParams: Record<string, ServerTableParamsType> = {
  services: {
    ...DefaultServerTableParams,
    filter: {
      regions: currentRegionId,
    },
  },
  dealers: {
    ...DefaultServerTableParams,
    filter: {
      region: currentRegionId,
    },
  },
};
provide("queriesTableParams", queriesTableParams);

const {
  data: services,
  isPending: isServicesPending,
  isFetching: isServicesFetching,
} = useQuery({
  queryKey: ["services"],
  queryFn: async (): Promise<PaginatedType<TService[]> | undefined> => {
    try {
      const [result, serviceTypes] = await Promise.all([
        ServiceService.service.getAll(queriesTableParams.services),
        ServiceService.serviceType.getAllSelectable(),
      ]);

      serviceTypeChoices.value = serviceTypes;

      return result.data;
    } catch (error) {
      snackStore.showError(error);
    }
  },
});

const {
  data: dealers,
  isPending: isDealerPending,
  isFetching: isDealerFetching,
} = useQuery({
  queryKey: ["dealers"],
  queryFn: async (): Promise<PaginatedType<TDealer[]> | undefined> => {
    try {
      const [result] = await Promise.all([DealerService.dealer.getAll(queriesTableParams.dealers)]);

      return result.data;
    } catch (error) {
      snackStore.showError(error);
    }
  },
});

onMounted(async () => {
  const [region, restOptions] = await Promise.all([
    RegionService.region.get(currentRegionId),
    RegionService.region.getOptions(),
  ]);

  entity.value = region.data;
  options.value = generateFormOptions(restOptions.data.actions.POST);

  fetching.value = false;
});
</script>

<template>
  <v-container fluid>
    <div class="tw:!mb-10">
      <v-card>
        <v-card-title class="tw:!px-6 tw:!py-8">
          <div class="tw:text-3xl">{{ t("models.region.typeof") }}</div>
        </v-card-title>
        <v-card-text class="tw:!px-6 tw:!pb-8">
          <v-skeleton-loader
            :loading="fetching"
            type="table-tbody"
          >
            <div class="w-100">
              <add-edit-region-form
                :item="entity"
                :loading="fetching"
                :options="options"
              />
            </div>
          </v-skeleton-loader>
        </v-card-text>
      </v-card>

      <div class="tw:!mt-10">
        <dealers-table
          :query-key="'dealers'"
          :data="dealers"
          headline-text="models.region.dealerTable"
          headline-class="tw:text-1xl tw:-mb-6"
        />
      </div>

      <div class="tw:!mt-10">
        <services-table
          :query-key="'services'"
          :data="services"
          :service-types="serviceTypeChoices"
          headline-text="models.region.serviceTable"
          headline-class="tw:text-1xl tw:-mb-6"
          :loading="fetching || isServicesPending || isServicesFetching"
        />
      </div>
    </div>
  </v-container>
</template>

<style scoped></style>
